import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CognitoUser,
  CreateUserRequest,
  CreateUserResponse,
  CustomerUserResponse,
  RegenerateTempPasswordResponse,
  UpdateUserRequest,
} from '@metron/shared';
import { useApiClient } from './useApiClient.ts';
import { toErrorMessage } from '@metron/shared';

export interface UserWithCustomers extends CognitoUser {
  customerData?: CustomerUserResponse;
  lastLoginAt?: string | null;
}

interface ListUsersWithCustomersResponse {
  users: UserWithCustomers[];
  paginationToken?: string;
}

/**
 * The one query every user-touching mutation invalidates.
 *
 * Exported so the writes that live outside this hook (`useCustomers`'s
 * user/customer, user/property and RBAC-role writes) invalidate the same key.
 */
export const USERS_QUERY_KEY = ['users', 'with-customers'] as const;

export function useUsers() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  // The single invalidation used by every mutation below. See lib/user-writes.ts
  // for the rule this enforces: one invalidation per save, after the last write.
  const invalidateUsers = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: USERS_QUERY_KEY });
  }, [queryClient]);

  // Fetch users query
  const {
    data: users = [],
    isLoading,
    error: queryError,
  } = useQuery({
    queryKey: USERS_QUERY_KEY,
    queryFn: async () => {
      const data = await apiClient.get<ListUsersWithCustomersResponse>(
        'users/with-customers',
      );
      return data.users;
    },
  });

  // Raw requests, with no invalidation of their own. They are the first write of
  // a two-write dialog save (see lib/user-writes.ts), where invalidating here
  // would race the second write: the refetch can land before the access write
  // does, leaving the freshly-saved customer/role data stale on screen.
  const createUserRequest = useCallback(
    (userData: CreateUserRequest): Promise<CreateUserResponse> =>
      apiClient.post<CreateUserResponse>('users', userData),
    [apiClient],
  );

  const updateUserRequest = useCallback(
    (userId: string, updates: UpdateUserRequest): Promise<CognitoUser> =>
      apiClient.put<CognitoUser>(`users/${encodeURIComponent(userId)}`, updates),
    [apiClient],
  );

  // Create user mutation
  const createUserMutation = useMutation({
    mutationFn: createUserRequest,
    onSuccess: invalidateUsers,
  });

  // Update user mutation
  const updateUserMutation = useMutation({
    mutationFn: ({
      userId,
      updates,
    }: {
      userId: string;
      updates: UpdateUserRequest;
    }): Promise<CognitoUser> => updateUserRequest(userId, updates),
    onSuccess: invalidateUsers,
  });

  // Delete user mutation (soft delete - disables the user)
  const deleteUserMutation = useMutation({
    mutationFn: async (userId: string): Promise<void> => {
      return await apiClient.delete<void>(
        `users/${encodeURIComponent(userId)}`,
      );
    },
    onSuccess: invalidateUsers,
  });

  // Permanent delete user mutation (hard delete - removes from Cognito)
  const permanentDeleteUserMutation = useMutation({
    mutationFn: async (userId: string): Promise<void> => {
      await apiClient.post<{ message: string }>(
        `users/${encodeURIComponent(userId)}/permanent-delete`,
      );
    },
    onSuccess: invalidateUsers,
  });

  // Reset password mutation
  // Invalidates: a reset moves the user back to FORCE_CHANGE_PASSWORD, which the
  // Status column renders.
  const resetPasswordMutation = useMutation({
    mutationFn: async (userId: string): Promise<void> => {
      await apiClient.post<{ message: string }>(
        `users/${encodeURIComponent(userId)}/reset-password`,
      );
    },
    onSuccess: invalidateUsers,
  });

  // Regenerate temporary password mutation
  // Invalidates: a new temporary password resets the user's Cognito status.
  const regenerateTempPasswordMutation = useMutation({
    mutationFn: async (userId: string): Promise<RegenerateTempPasswordResponse> => {
      return await apiClient.post<RegenerateTempPasswordResponse>(
        `users/${encodeURIComponent(userId)}/regenerate-temp-password`,
      );
    },
    onSuccess: invalidateUsers,
  });

  // Resend welcome email mutation
  // Invalidates: resending re-issues the temporary password, so the user's
  // status and last-modified timestamp change server-side.
  const resendWelcomeEmailMutation = useMutation({
    mutationFn: async (userId: string): Promise<void> => {
      await apiClient.post<{ message: string }>(
        `users/${encodeURIComponent(userId)}/resend-welcome-email`,
      );
    },
    onSuccess: invalidateUsers,
  });

  // Error handling - convert query error to string for backward compatibility
  const error = queryError ? (toErrorMessage(queryError, 'An error occurred')) : null;

  return {
    users,
    isLoading,
    error,
    invalidateUsers,
    createUser: createUserMutation.mutateAsync,
    updateUser: (userId: string, updates: UpdateUserRequest) =>
      updateUserMutation.mutateAsync({ userId, updates }),
    // Same requests as createUser/updateUser, minus the invalidation. Used as the
    // first write of a composite dialog save (lib/user-writes.ts), which owns the
    // single invalidation for the whole sequence.
    createUserWithoutInvalidation: createUserRequest,
    updateUserWithoutInvalidation: updateUserRequest,
    deleteUser: deleteUserMutation.mutateAsync,
    permanentDeleteUser: permanentDeleteUserMutation.mutateAsync,
    resetPassword: resetPasswordMutation.mutateAsync,
    regenerateTempPassword: regenerateTempPasswordMutation.mutateAsync,
    resendWelcomeEmail: resendWelcomeEmailMutation.mutateAsync,
  };
}
